// MDAccordionSimple.js

import React, { forwardRef, useState, useEffect } from "react";
import MDAccordionRoot from "components/MDAccordion/MDAccordionRoot";
import MDTypography from "components/MDTypography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  MenuItem,
  TextareaAutosize,
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import MDBox from "components/MDBox";
import { format, parseISO } from "date-fns";
import { NumericFormat } from "react-number-format";

const MDAccordionSimple = forwardRef(
  (
    { color = "info", title, fields, nestedTitle, data, handleInputChange, ...rest },
    ref
  ) => {
    const [localValue, setLocalValue] = useState("");

    const handleLocalChange = (field, value) => {
      setLocalValue((prev) => ({ ...prev, [field]: value }));
      handleInputChange(field, value);
    };

    return (
      <MDAccordionRoot {...rest} ref={ref} ownerState={{ color }}>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${title}-content`}
            id={`${title}-header`}
          >
            <MDTypography variant="h6" color={color === "white" ? "dark" : "white"}>
              {title}
            </MDTypography>
          </AccordionSummary>
          <AccordionDetails>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
              {Array.isArray(fields[0]) ? (
                fields.map((fieldGroup, groupIndex) => (
                  <MDBox key={groupIndex} mb={2}>
                    {nestedTitle && nestedTitle[groupIndex] && (
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="medium"
                        mb={1}
                      >
                        {nestedTitle[groupIndex]}
                      </MDTypography>
                    )}
                    <Grid container spacing={2}>
                      {fieldGroup.map((field) => (
                        <Grid item xs={12} sm={12} md={12} lg={3} key={field.name}
                        >
                          <MDBox mb={1} fullWidth>
                            <MDTypography
                              variant="caption"
                              fontWeight="medium"
                              color="text"
                              textTransform="uppercase"
                              fullWidth
                            >
                              {field.label}
                            </MDTypography>
                            {field.type === "date" ? (
                              <DatePicker
                                format="dd/MM/yyyy"
                                value={data[field.name] ? parseISO(data[field.name]) : null}
                                onChange={(newValue) => {
                                  if (field.editable) {
                                    handleInputChange(
                                      field.name,
                                      newValue ? newValue.toISOString().split("T")[0] : null
                                    );
                                  }
                                }}
                                sx={{
                                  // Não force altura aqui
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: field.editable ? "inherit" : "#f0f0f0",
                                    "& fieldset": {
                                      borderColor: "rgba(0, 0, 0, 0.12)",
                                    },
                                  },
                                }}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    size: "small",
                                    variant: "outlined",
                                    sx: {
                                      backgroundColor: field.editable ? "inherit" : "#f0f0f0",
                                      color: field.editable ? "inherit" : "black",
                                      "& .MuiOutlinedInput-root": {
                                        "& fieldset": { borderColor: "rgba(0, 0, 0, 0.12)" },
                                      },
                                      "& input": {
                                        padding: "8.5px 14px", // Mesmo padding do TextField size="small"
                                      },
                                    },
                                  },
                                }}
                              />
                            ) : field.type === "select" ? (
                              <select
                                name={field.name}
                                value={data[field.name]}
                                disabled={!field.editable}
                                onChange={(e) =>
                                  handleInputChange(field.name, e.target.value)
                                }
                                style={{
                                  fontFamily:
                                    '"Roboto", "Helvetica", "Arial", "sans-serif"',
                                  fontSize: "0.75rem",
                                  fontWeight: "400",
                                  lineHeight: "1.4375em",
                                  letterSpacing: "0.00938em",
                                  borderColor: "rgb(197 197 197)",
                                  boxSizing: "border-box",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  width: "100%",
                                  position: "relative",
                                  borderRadius: "4px",
                                  backgroundColor: field.editable ? "inherit" : "#f0f0f0",
                                  // borderRadius: "0.375rem"
                                  marginTop: "4px",
                                  height: "3.15em",
                                  color: field.editable ? "inherit" : "#f0f0f0",
                                  padding: "0.5rem",
                                  '& .MuiInputBaseInput.MuiDisabled': {
                                    color: field.editable ? "inherit" : "#f0f0f0", // Define o texto como preto quando desabilitado
                                  },
                                  '& .MuiOutlinedInputNotchedOutline.MuiDisabled': {
                                    borderColor: '#ced4da',
                                  },
                                }}
                              >
                                {field.options.map((option, index) => (
                                  <option key={index} value={option.Id}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      color="text"
                                      sx={{
                                        backgroundColor: field.editable ? "inherit" : "#f0f0f0",
                                        color: field.editable ? "inherit" : "black",
                                        "& .MuiOutlinedInput-root": {
                                          "& fieldset": { borderColor: "rgba(0, 0, 0, 0.12)" },
                                        },
                                      }}
                                    >
                                      {option.Description}
                                    </MDTypography>
                                  </option>
                                ))}
                              </select>
                            ) : field.type === "currency" ? (
                              <NumericFormat
                                customInput={TextField}
                                fullWidth
                                size="small"
                                variant="outlined"
                                value={data[field.name] || ""}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                allowNegative={false}
                                prefix="R$ "
                                onValueChange={(values) => {
                                  if (field.editable) {
                                    handleInputChange(
                                      field.name,
                                      values.value
                                    );
                                  }
                                }}
                                disabled={!field.editable}
                                sx={{
                                  mt: 0.5,
                                  backgroundColor: field.editable
                                    ? "inherit"
                                    : "#f0f0f0",
                                  "& .MuiOutlinedInputNotchedOutline.MuiDisabled":
                                  {
                                    borderColor: "#ced4da",
                                  },
                                }}
                              />
                            ) : (
                              <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                multiline={field.multiLine?.multiline}
                                inputProps={{
                                  maxLength: field.multiLine?.maxLength,
                                  readOnly: !field.editable
                                }}
                                type={field.type}
                                value={data[field.name] || ""}
                                onChange={(e) => {
                                  handleInputChange(
                                    field.name,
                                    e.target.value
                                  );
                                }}
                                sx={{
                                  backgroundColor: field.editable ? "inherit" : "#f0f0f0",
                                  color: field.editable ? "inherit" : "#f0f0f0",
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "rgba(0, 0, 0, 0.12)" },
                                  },
                                }}
                              />
                            )}
                          </MDBox>
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                ))
              ) : (
                <Grid container spacing={2}>
                  {fields.map((field) => (
                    
                    <Grid item xs={12} sm={12} md={12} lg={3} key={field.name}
                    >
                      <MDBox mb={1} fullWidth>
                        <MDTypography
                          variant="caption"
                          fontWeight="medium"
                          color="text"
                          textTransform="uppercase"
                          fullWidth
                          sx={{
                            backgroundColor: "transparent", // Remove o fundo
                            border: "none", // Remove a borda
                            boxShadow: "none", // Remove qualquer sombra
                            padding: "0", // Remove o padding
                            margin: "0", // Remove a margem
                          }}
                        >
                          {field.label}
                        </MDTypography>
                        {field.type === "date" ? (
                          <DatePicker
                            format="dd/MM/yyyy"
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 37,
                              },
                              mt: 0.5,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: field.editable
                                  ? "inherit"
                                  : "#f0f0f0",
                                "& fieldset": {
                                  borderColor: "rgba(0, 0, 0, 0.12)",
                                },
                              },
                            }}
                            value={
                              data[field.name]
                                ? parseISO(data[field.name])
                                : null
                            }
                            onChange={(newValue) => {
                              if (field.editable) {
                                handleInputChange(
                                  field.name,
                                  newValue
                                    ? newValue.toISOString().split("T")[0]
                                    : null
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                size="small"
                                variant="outlined"
                                sx={{
                                  backgroundColor: field.editable ? "inherit" : "#f0f0f0",
                                  color: field.editable ? "inherit" : "black",
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "rgba(0, 0, 0, 0.12)" },
                                  },
                                }}
                              />
                            )}
                          />
                        ) : field.type === "select" ? (
                          <select
                            name={field.name}
                            value={data[field.name]}
                            onChange={(e) =>
                              handleInputChange(field.name, e.target.value)
                            }
                            disabled={!field.editable}
                            style={{
                              fontFamily:
                                '"Roboto", "Helvetica", "Arial", "sans-serif"',
                              fontSize: "0.75rem",
                              fontWeight: "400",
                              lineHeight: "1.4375em",
                              letterSpacing: "0.00938em",
                              borderColor: "rgb(197 197 197)",
                              boxSizing: "border-box",
                              display: "inline-flex",
                              alignItems: "center",
                              width: "100%",
                              position: "relative",
                              borderRadius: "0.375rem",
                              backgroundColor: field.editable ? "inherit" : "#f0f0f0",
                              height: "35px",
                              color: field.editable ? "inherit" : "black", // Alterado para garantir que o texto seja preto quando não editável
                              padding: "0.5rem",
                            }}
                          >
                            {field.options.map((option, index) => (
                              <option key={index} value={option.Id}>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="medium"
                                  color="text"
                                  style={{ color: "black" }} // Define a cor do texto como preto
                                >
                                  {option.Description}
                                </MDTypography>
                              </option>
                            ))}
                          </select>
                        ) : field.type === "currency" ? (
                          <NumericFormat
                            customInput={TextField}
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={data[field.name] || ""}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            allowNegative={false}
                            prefix="R$ "
                            onValueChange={(values) => {
                              if (field.editable) {
                                handleInputChange(
                                  field.name,
                                  values.value
                                );
                              }
                            }}
                            disabled={!field.editable}
                            sx={{
                              mt: 0.5,
                              backgroundColor: field.editable
                                ? "inherit"
                                : "#f0f0f0",
                              "& .MuiOutlinedInputNotchedOutline.MuiDisabled":
                              {
                                borderColor: "#ced4da",
                              },
                            }}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            multiline={field.multiLine?.multiline}
                            inputProps={{
                              maxLength: field.multiLine?.maxLength,
                            }}
                            size="small"
                            variant="outlined"
                            value={localValue[field.name] || data[field.name]}
                            onChange={(e) => {
                              handleLocalChange(field.name, e.target.value);
                            }}
                            disabled={!field.editable}
                            sx={{
                              backgroundColor: field.editable ? "inherit" : "#f0f0f0",
                              color: field.editable ? "inherit" : "black",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": { borderColor: "rgba(0, 0, 0, 0.12)" },
                              },
                            }}
                          />
                        )}
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
              )}
            </LocalizationProvider>
          </AccordionDetails>
        </Accordion>
      </MDAccordionRoot>
    );
  }
);

export default MDAccordionSimple;